// src/components/InvestmentPortfolio.js - VERSION FORCÉE (STYLE INLINE)

import React, { useState } from 'react';
import { Card, Button, ProgressBar, Row, Col } from 'react-bootstrap';
import { FaCopy, FaCheckCircle, FaUsers, FaDollarSign, FaArrowRight } from 'react-icons/fa'; 

// ... (fonctions helper et hooks useState inchangés)

const formatCurrency = (amount) => {
    return amount ? parseFloat(amount).toLocaleString('fr-FR', { style: 'currency', currency: 'XOF' }) : '0 FCFA';
};

const InvestmentPortfolio = ({ investments, onNavigate }) => { 
    const [copySuccess, setCopySuccess] = useState('');
    const [copiedPlanId, setCopiedPlanId] = useState(null); 

    // ... (copyToClipboard et handleCardClick inchangés)
    const copyToClipboard = (e, link, planId) => {
        e.stopPropagation(); 
        
        navigator.clipboard.writeText(link)
            .then(() => {
                setCopiedPlanId(planId);
                setCopySuccess('Lien copié !');
                setTimeout(() => {
                    setCopySuccess('');
                    setCopiedPlanId(null);
                }, 2000);
            })
            .catch(err => {
                console.error('Erreur lors de la copie:', err);
                setCopySuccess('Échec de la copie.');
                setTimeout(() => setCopySuccess(''), 3000);
            });
    };

    const handleCardClick = (invId) => {
        if (onNavigate) {
            onNavigate(`/mes-investissements/${invId}`);
        }
    };
    
    const activeInvestments = investments.filter(inv => inv.status === 'active' || inv.status === 'pending');

    if (!activeInvestments || activeInvestments.length === 0) {
        return (
            <div className="alert alert-info text-center small py-3" role="alert">
                Vous n'avez pas encore d'investissement actif.
            </div>
        );
    }
    
    const displayInvestments = activeInvestments.slice(0, 3);

    return (
        <div className="portfolio-list">
            {displayInvestments.map((inv) => {
                const isCopied = copiedPlanId === inv._id;
                const sponsoredGoal = 2;
                const sponsoredCount = inv.sponsoredCount || 0;
                const sponsorshipPercentage = Math.min(100, (sponsoredCount / sponsoredGoal) * 100);
                
                let fullReferralLink = null;
                if (inv.referralLink && inv.planName && inv.amountInOriginalCurrency) {
                    const planNameEncoded = encodeURIComponent(inv.planName);
                    const investmentAmount = parseFloat(inv.amountInOriginalCurrency);
                    fullReferralLink = `${window.location.origin}/investment/${planNameEncoded}/${investmentAmount}?referral=${inv.referralLink}`;
                }
                
                let statusBadgeClass = 'bg-secondary';
                let statusText = 'Inconnu';
                if (inv.status === 'active') {
                    statusBadgeClass = 'bg-primary';
                    statusText = 'Actif';
                } else if (inv.status === 'pending') {
                    statusBadgeClass = 'bg-warning text-dark';
                    statusText = 'En attente';
                }
                
                const chainCompleted = inv.chainCompleted || false;

                return (
                    <Card 
                        key={inv._id} 
                        // 🚨 FORÇAGE : Marge verticale réduite à 0.5rem (mb-2)
                        className="shadow-sm mb-2 border-0 portfolio-card-clickable" 
                        style={{ 
                            borderLeft: `4px solid ${inv.status === 'active' ? '#007bff' : '#6c757d'}`,
                            cursor: 'pointer'
                        }}
                        onClick={() => handleCardClick(inv._id)}
                    >
                        {/* 🚨 FORÇAGE : Padding à 10px sur le Card Body */}
                        <Card.Body style={{ padding: '10px' }}> 
                            
                            {/* EN-TÊTE : Nom du plan et Statut */}
                            <div className="d-flex justify-content-between align-items-start" style={{ marginBottom: '5px' }}>
                                <h6 className="fw-bold mb-0 text-dark fs-6">
                                    {inv.planName}
                                </h6>
                                <span className={`badge ${statusBadgeClass} rounded-pill px-2 py-1 small fw-semibold`}>
                                    {statusText}
                                </span>
                            </div>

                            {/* Montant investi */}
                            <p className="text-primary fw-bold small mb-2 border-bottom pb-1" style={{ marginBottom: '5px' }}> 
                                <FaDollarSign className="me-1" />
                                {formatCurrency(inv.amountInOriginalCurrency)}
                            </p>

                            {/* PROGRESSION / CHAINAGE */}
                            <div style={{ marginBottom: '8px', paddingTop: '5px' }} className="border-top"> 
                                <div className="d-flex justify-content-between small text-muted mb-1">
                                    <span className="fw-semibold small">Progression parrainage (Direct):</span>
                                </div>
                                
                                {chainCompleted ? (
                                    <span className="text-success fw-bold small d-block">
                                        Chaîne complétée <FaCheckCircle className="ms-1" />
                                    </span>
                                ) : (
                                    <>
                                        <div className="d-flex justify-content-between small text-dark mb-1">
                                            <span><FaUsers className="me-1" /> Parrainés</span>
                                            <span className="fw-bold">{sponsoredCount} / {sponsoredGoal}</span>
                                        </div>
                                        <ProgressBar 
                                            now={sponsorshipPercentage} 
                                            variant={sponsoredCount >= sponsoredGoal ? 'success' : 'info'} 
                                            style={{ height: '5px' }}
                                        />
                                    </>
                                )}
                            </div>
                            
                            {/* ACTION : COPIER LE LIEN & VOIR LES DÉTAILS */}
                            <Row className="g-1" style={{ marginTop: '5px' }}> 
                                
                                {/* Colonne du lien de parrainage */}
                                {fullReferralLink && (
                                    <Col xs={8}>
                                        <div className="input-group input-group-sm">
                                            <input
                                                type="text"
                                                className="form-control form-control-sm text-truncate"
                                                value={fullReferralLink}
                                                readOnly
                                                style={{ 
                                                    backgroundColor: isCopied ? '#e9f7ef' : '#f8f9fa',
                                                    // 🚨 FORÇAGE : Pas de marge interne par défaut sur l'input
                                                    padding: '0.25rem 0.5rem' 
                                                }} 
                                                title={fullReferralLink}
                                            />
                                            <Button
                                                variant={isCopied ? 'success' : 'primary'}
                                                onClick={(e) => copyToClipboard(e, fullReferralLink, inv._id)} 
                                                title="Copier le lien"
                                                style={{ width: '35px', flexGrow: 0 }}
                                            >
                                                <FaCopy />
                                            </Button>
                                        </div>
                                    </Col>
                                )}
                                
                                {/* Colonne du bouton de détails */}
                                <Col xs={4}>
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        className="w-100"
                                        onClick={() => handleCardClick(inv._id)}
                                    >
                                        Détails <FaArrowRight className='ms-1' />
                                    </Button>
                                </Col>
                            </Row>

                        </Card.Body>
                    </Card>
                );
            })}
            
            {copySuccess && (
                <div className="copy-alert alert alert-success mt-3 text-center small py-2">{copySuccess}</div>
            )}
        </div>
    );
};

export default InvestmentPortfolio;